import { isNil } from 'lodash-es'
import { NegotiationDetail, RepoType, ValidationIssue } from 'api'
import { notNilFilter } from 'utils'
import { createInvalidValueValidationIssue, Severity } from 'model'
import { Locale, Translation } from 'locale'
import { IncomingPriceUpload, IncomingSpreadsheetParseResult } from './IncomingUploadDialog'
import { isNotEmpty, isNotNil } from 'utils/predicate'
import { canEditNegotiationDetailSettlementType } from '../datagrid/modelUtils'

export enum PendingIncomingFields {
  RFQ_ID = 'RFQ_ID',
  NEGOTIATION_ID = 'NEGOTIATION_ID',
  RATE = 'RATE',
  HAIRCUT = 'HAIRCUT',
  HAIRCUT_TYPE = 'HAIRCUT_TYPE',
  SETTLEMENT_TYPE = 'SETTLEMENT_TYPE'
}

export const validateIncomingParseResult = (
  parseResult: IncomingSpreadsheetParseResult<IncomingPriceUpload>,
  negotiationDetailGetters: {
    getIncomingNegotiationDetailsByRfqId: (rfqId: string) => NegotiationDetail[]
    getIncomingNegotiationDetailsByCollectionId: (collectionId: string) => NegotiationDetail[]
    getIncomingNegotiationDetailsByNegotiationId: (negotiationId: string) => NegotiationDetail[]
  }
): IncomingPriceUpload[] => {
  const { priceUploads } = parseResult

  const { getIncomingNegotiationDetailsByRfqId, getIncomingNegotiationDetailsByNegotiationId } = negotiationDetailGetters
  if (priceUploads.length === 0) {
    return []
  }

  const allRfqIds = priceUploads.map(priceUpdate => priceUpdate.rfqId).filter(notNilFilter)
  const allNegotiationIds = priceUploads.map(priceUpdate => priceUpdate.negotiationId).filter(notNilFilter)

  return priceUploads.map(priceUpload => {
    const dataIssues: ValidationIssue[] = []
    const isStockBorrowingLendingNegotiation =
      !isNil(priceUpload.rfqId) &&
      !!getIncomingNegotiationDetailsByRfqId(priceUpload.rfqId).find(n => n.negotiation.repoType === RepoType.STOCK_BORROWING_LENDING)

    if (allRfqIds.filter(rfqId => rfqId === priceUpload.rfqId).length > 1) {
      dataIssues.push(createInvalidValueValidationIssue(PendingIncomingFields.RFQ_ID, Locale.translate(Translation.SC_DUPLICATE_RFQ_ID)))
    }

    if (allNegotiationIds.filter(negotiationId => negotiationId === priceUpload.negotiationId).length > 1) {
      dataIssues.push(
        createInvalidValueValidationIssue(PendingIncomingFields.NEGOTIATION_ID, Locale.translate(Translation.SC_DUPLICATE_NEGOTIATION_ID))
      )
    }

    if (isNil(priceUpload.rfqId) && isNil(priceUpload.negotiationId)) {
      dataIssues.push(
        createInvalidValueValidationIssue(PendingIncomingFields.RFQ_ID, Locale.translate(Translation.SC_MISSING_RFQ_AND_NEGOTIATION_ID)),
        createInvalidValueValidationIssue(
          PendingIncomingFields.NEGOTIATION_ID,
          Locale.translate(Translation.SC_MISSING_RFQ_AND_NEGOTIATION_ID)
        )
      )
    }

    if (isNotNil(priceUpload.rfqId) && getIncomingNegotiationDetailsByRfqId(priceUpload.rfqId).length === 0) {
      dataIssues.push(
        createInvalidValueValidationIssue(
          PendingIncomingFields.RFQ_ID,
          Locale.translate(Translation.SC_INVALID_RFQ_ID, { rfqId: priceUpload.rfqId })
        )
      )
    }

    if (isNotNil(priceUpload.negotiationId) && getIncomingNegotiationDetailsByNegotiationId(priceUpload.negotiationId).length === 0) {
      dataIssues.push(
        createInvalidValueValidationIssue(
          PendingIncomingFields.NEGOTIATION_ID,
          Locale.translate(Translation.SC_INVALID_NEGOTIATION_ID, { negotiationId: priceUpload.negotiationId })
        )
      )
    }

    if (isStockBorrowingLendingNegotiation) {
      dataIssues.push(
        createInvalidValueValidationIssue(PendingIncomingFields.RFQ_ID, Locale.translate(Translation.SC_INVALID_STOCK_BORROWING_LENDING_ID))
      )
    }

    if (isNil(priceUpload.rate)) {
      dataIssues.push(createInvalidValueValidationIssue(PendingIncomingFields.RATE, Locale.translate(Translation.SC_MISSING_RATE)))
    }

    if ((isNotNil(priceUpload.rfqId) || isNotNil(priceUpload.negotiationId)) && isNotNil(priceUpload.settlementType)) {
      const negotiationDetailsByRfqOrNegotiationId = priceUpload.rfqId
        ? getIncomingNegotiationDetailsByRfqId(priceUpload.rfqId)
        : priceUpload.negotiationId
        ? getIncomingNegotiationDetailsByNegotiationId(priceUpload.negotiationId)
        : []
      if (
        isNotEmpty(negotiationDetailsByRfqOrNegotiationId) &&
        !negotiationDetailsByRfqOrNegotiationId.every(canEditNegotiationDetailSettlementType)
      ) {
        dataIssues.push(
          createInvalidValueValidationIssue(
            PendingIncomingFields.SETTLEMENT_TYPE,
            Locale.translate(Translation.SC_SETTLEMENT_TYPE_NOT_EDITABLE),
            Severity.WARN
          )
        )
      }
    }

    return {
      ...priceUpload,
      dataIssues
    }
  })
}
